import React, { useCallback, useMemo, useState } from "react";

import { SatelliteDateRange, SatelliteImageLayer } from "services";

import { ArrayUtils, formatDayDate, useGetFieldPractices, useGetFieldSatelliteObservations, useGetFieldSatelliteThumbnails } from "utils";

import { Alert, Button, Icon, LazyLoadImage, Segmented, SkeletonWrapper, TagNormal, Typography } from "components";

import { RemoteSensingDetections } from "pages/admin/verifications/components/automations/RemoteSensingDetections";
import { SatelliteChart } from "pages/admin/verifications/components/automations/SatelliteChart";
import { SatelliteDayModal } from "pages/admin/verifications/components/automations/SatelliteDayModal";
import { CoverCropChip } from "pages/admin/verifications/utils/coverCropChips";
import { getSeasonRange, getYearAfter, getYearBefore } from "pages/admin/verifications/utils/satellite";

type RemoteSensingEvidenceTabProps = {
  farmId: string;
  fieldId: string;
  fieldName: string;
  seasonYear: number;
  coverCropWindowStart?: string | null;
  coverCropChip?: CoverCropChip | null;
};

const LAYER_OPTIONS: { value: SatelliteImageLayer; label: string }[] = [
  { value: "true-color", label: "Natural colour" },
  { value: "ndvi", label: "NDVI" },
];

// A month shows at most a 3 x 3 grid of pictures. The last cell says how many more there are; the day modal steps through all.
const MONTH_GRID_SIZE = 9;

const LoadError = ({ headline, onRetry }: { headline: string; onRetry: () => void }) => (
  <Alert
    className={undefined}
    closable={false}
    type="red"
    headline={headline}
    description={
      <Button type="secondary" size="small" onClick={onRetry}>
        Try again
      </Button>
    }
  />
);

export const RemoteSensingEvidenceTab = ({ farmId, fieldId, fieldName, seasonYear, coverCropWindowStart, coverCropChip }: RemoteSensingEvidenceTabProps) => {
  const [layer, setLayer] = useState<SatelliteImageLayer>("true-color");
  const [openDate, setOpenDate] = useState<string | null>(null);

  // The season, plus the years the verifier added before or after it. Derived from the counts, so the ranges always
  // join up. Each range is fetched and cached on its own.
  const today = useMemo(() => new Date().toISOString().slice(0, 10), []);
  const [addedYears, setAddedYears] = useState({ before: 0, after: 0 });
  const ranges = useMemo(() => {
    const season = getSeasonRange(seasonYear, today);
    const before: SatelliteDateRange[] = [];
    const after: SatelliteDateRange[] = [];
    for (let earlier = getYearBefore(season); earlier && before.length < addedYears.before; earlier = getYearBefore(earlier)) before.unshift(earlier);
    for (let later = getYearAfter(season, today); later && after.length < addedYears.after; later = getYearAfter(later, today)) after.push(later);
    return [...before, season, ...after];
  }, [seasonYear, today, addedYears]);
  const canAddEarlier = !!getYearBefore(ranges[0] as SatelliteDateRange);
  const canAddLater = !!getYearAfter(ranges[ranges.length - 1] as SatelliteDateRange, today);

  const { observations, results: observationResults } = useGetFieldSatelliteObservations({ farmId, fieldId, ranges });
  const {
    thumbnailsByMonth,
    isLoading: areThumbnailsLoading,
    failed: failedThumbnails,
  } = useGetFieldSatelliteThumbnails({ farmId, fieldId, ranges, layer, isReady: observationResults.map(({ isSuccess }) => isSuccess) });
  const { data: practices } = useGetFieldPractices({ fieldId, inView: true, isAdmin: true });

  // The buttons only add years that exist, so the season sits after exactly the added earlier years.
  const season = observationResults[addedYears.before];
  const isAddingYear = observationResults.some(({ isLoading }) => isLoading);

  const crops = useMemo(() => {
    const [fromYear, toYear] = [Number(observations?.range.from.slice(0, 4)), Number(observations?.range.to.slice(0, 4))];
    return (practices?.crops ?? []).filter(({ year }) => year !== undefined && year >= fromYear && year <= toYear);
  }, [practices, observations]);
  const imageDaysByMonth = useMemo(
    () => ArrayUtils.groupBy(observations?.passes.filter(({ hasImage }) => hasImage) ?? [], ({ date }) => date.slice(0, 7)),
    [observations]
  );
  const renderMonth = useCallback(
    (month: string) => {
      const monthThumbnails = thumbnailsByMonth[month] ?? [];
      const imageDays = imageDaysByMonth[month]?.length ?? 0;
      const hidden = monthThumbnails.length - MONTH_GRID_SIZE;
      return (
        <div className="satellite-month">
          <div className="satellite-month-days">
            {!monthThumbnails.length && areThumbnailsLoading
              ? Array.from({ length: Math.min(imageDays, MONTH_GRID_SIZE) }, (_, index) => <div key={index} className="satellite-thumbnail" />)
              : monthThumbnails.slice(0, MONTH_GRID_SIZE).map(({ date, url }, index) => {
                  const day = formatDayDate(date);
                  return (
                    <button key={date} type="button" className="satellite-thumbnail" title={day} aria-label={`Open ${day}`} onClick={() => setOpenDate(date)}>
                      <LazyLoadImage src={url} alt={day} width="100%" height="100%" />
                      {hidden > 0 && index === MONTH_GRID_SIZE - 1 && <span className="satellite-thumbnail-more">+{hidden + 1}</span>}
                    </button>
                  );
                })}
          </div>
          <Typography variant="paragraph-s" color="grey-600">
            {imageDays ? `${imageDays} picture${imageDays === 1 ? "" : "s"}` : "No pictures"}
          </Typography>
        </div>
      );
    },
    [thumbnailsByMonth, imageDaysByMonth, areThumbnailsLoading]
  );

  const detections = coverCropChip && practices && (
    <RemoteSensingDetections
      farmId={farmId}
      fieldId={fieldId}
      seasonYear={seasonYear}
      chip={coverCropChip}
      cropsOfYear={practices.crops.filter(({ year }) => year === seasonYear)}
    />
  );

  const renderObservations = () => {
    if (season?.isError) return <LoadError headline="The satellite observations didn't load" onRetry={() => season.refetch()} />;
    if (!observations || !season?.data) return <SkeletonWrapper isLoading type="paragraph" rows={10} />;

    const { range, passes } = observations;
    const failedYears = observationResults.filter(({ isError }) => isError);

    return (
      <>
        <div className="satellite-evidence-header">
          <Icon iconName="tabler/satellite" size="m" />
          <Typography variant="h5">Satellite observations</Typography>
          <TagNormal
            className="satellite-evidence-badge"
            variant="paragraph-s"
            color="blue"
            text={`${passes.filter(({ hasImage }) => hasImage).length} clear passes`}
          />
        </div>
        <Typography variant="paragraph-s" color="grey-600">
          Sentinel-2 NDVI and Sentinel-1 radar over the field, {formatDayDate(range.from)} to {formatDayDate(range.to)}. Boxes show the platform crops, and a
          dotted NDVI line means cloud with no picture.
        </Typography>

        <div className="satellite-pictures-header">
          <Typography variant="paragraph-m" bold>
            Pictures under each month
          </Typography>
          <Segmented<SatelliteImageLayer> size="small" value={layer} onChange={setLayer} options={LAYER_OPTIONS} />
        </div>

        <div className="satellite-range-controls">
          {canAddEarlier && (
            <Button
              type="secondary"
              size="small"
              iconLeft
              icon={<Icon iconName="tabler/chevron-left" size="s" />}
              loading={isAddingYear}
              disabled={isAddingYear || failedYears.length > 0}
              onClick={() => setAddedYears((current) => ({ ...current, before: current.before + 1 }))}
            >
              Earlier year
            </Button>
          )}
          {canAddLater && (
            <Button
              type="secondary"
              size="small"
              className="satellite-range-later"
              icon={<Icon iconName="tabler/chevron-right" size="s" />}
              loading={isAddingYear}
              disabled={isAddingYear || failedYears.length > 0}
              onClick={() => setAddedYears((current) => ({ ...current, after: current.after + 1 }))}
            >
              Later year
            </Button>
          )}
        </div>

        <SatelliteChart
          observations={observations}
          crops={crops}
          coverCropWindowStart={coverCropWindowStart}
          onPassClick={setOpenDate}
          renderMonth={renderMonth}
        />
        {failedYears.length > 0 && <LoadError headline="The added year didn't load" onRetry={() => failedYears.forEach(({ refetch }) => refetch())} />}
        {failedThumbnails.length > 0 && <LoadError headline="Some pictures didn't load" onRetry={() => failedThumbnails.forEach(({ refetch }) => refetch())} />}

        <SatelliteDayModal
          farmId={farmId}
          fieldId={fieldId}
          fieldName={fieldName}
          observations={observations}
          crops={crops}
          coverCropWindowStart={coverCropWindowStart}
          date={openDate}
          onDateChange={setOpenDate}
        />
      </>
    );
  };

  return (
    <>
      {detections}
      {renderObservations()}
    </>
  );
};
